import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewContainerRef
} from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {TypeaheadComponent} from './typeahead.component';

@Directive({
  selector: 'mtg-text-field[mtgTypeahead]'
})
export class TypeaheadDirective implements OnInit, OnDestroy {
  private overlayRef: OverlayRef;
  private subscriptions: (() => void)[] = [];

  @Input('mtgTypeahead')
  typeahead: TypeaheadComponent;

  @HostListener('focus')
  onFocus() {
    this.attachOverlay();
  }

  constructor(
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.renderer.listen('window', 'click', $event => {
        if (!this.overlayRef) {
          return;
        }
        const target = $event.composedPath() && $event.composedPath()[0] || $event.target;
        if (!this.elementRef.nativeElement.contains(target)) {
          this.overlayRef.dispose();
          if (this.typeahead) {
            this.typeahead.isVisible = false;
          }
          this.cdr.markForCheck();
        }
      })
    );
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
    for (const unsubscribe of this.subscriptions) {
      unsubscribe();
    }
    if (this.typeahead) {
      this.typeahead.isVisible = false;
    }
  }

  private attachOverlay() {
    const templatePortal = new TemplatePortal(this.typeahead.template, this.viewContainerRef);
    this.overlayRef = this.overlay.create(this._getOverlayConfig());
    this.overlayRef.attach(templatePortal);
    this.typeahead.isVisible = true;
  }

  private _getOverlayConfig() {
    return new OverlayConfig({
      positionStrategy: this.getPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this._getHostWidth(),
    });
  }

  private getPositionStrategy() {
    return this.overlay.position()
      .flexibleConnectedTo(this.elementRef.nativeElement)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top'
        },
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
        }
      ]);
  }

  /** Returns the width of the input element, so the panel width can match it. */
  private _getHostWidth(): number {
    return this.elementRef.nativeElement.getBoundingClientRect().width;
  }
}
